import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { debounceTime, tap, takeUntil } from 'rxjs/operators';
import { fromEvent, Subject } from 'rxjs';
import { SedsColorMode } from '../../shared/enums/seds-color-mode.enum';
import { SedsColorModeType } from '../../shared/types/seds-color-mode.type';

@Component({
    selector: 'seds-timer',
    templateUrl: './seds-timer.component.html',
    styleUrls: ['./seds-timer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SedsTimerComponent implements OnDestroy, OnInit {
  @Input() colorMode: SedsColorMode | SedsColorModeType = SedsColorMode.DEFAULT_LIGHT;
  @Output() finishedTime: EventEmitter<number> = new EventEmitter<number>();

  public ariaLabel: string = '';
  public percentageBar!: number;
  private destroy$ = new Subject<void>();
  private endTime: number = 0;
  private lastVisibilityChange: number = 0;
  private startValue!: number;
  private timerInterval!: any;
  private visibilitySubject = new Subject<boolean>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.setupVisibilityChangeListener();
  }

  private setupVisibilityChangeListener(): void {
    fromEvent(document, 'visibilitychange')
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.lastVisibilityChange = Date.now();
          this.visibilitySubject.next(document.visibilityState === 'visible');
        })
      )
      .subscribe();

    this.visibilitySubject
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        tap(isVisible => this.handleVisibilityChange(isVisible))
      )
      .subscribe();
  }

  public start(timeValue: number): void {
    this.startValue = timeValue * 60;
    this.endTime = Date.now() + this.startValue * 1000;
    this.startTimer();
  }

  private startTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      this.updateTimer();
    }, 1000);
  }

  private updateTimer(): void {
    const now = Date.now();
    const remainingSeconds = Math.max(0, Math.round((this.endTime - now) / 1000));

    if (remainingSeconds <= 0) {
      this.stop();
      this.finishedTime.emit(0);
    } else {
      this.updateProgressBar(remainingSeconds);
    }
    this.updateAriaLabel();
    this.cdr.detectChanges();
  }

  stop() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.percentageBar = 0;
    }
    this.cdr.markForCheck();
  }

  formatTime(): string {
    const remainingSeconds = Math.max(0, Math.round((this.endTime - Date.now()) / 1000));
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    return [hours, minutes, seconds]
      .map(v => (v < 10 ? '0' + v : v))
      .filter((v, i) => v !== '00' || i > 0)
      .join(':');
  }

  getTimeComponents(): { hours: number; minutes: number; seconds: number } {
    const remainingSeconds = Math.max(0, Math.round((this.endTime - Date.now()) / 1000));
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    return { hours, minutes, seconds };
  }

  updateProgressBar(remainingSeconds: number) {
    this.percentageBar = (remainingSeconds / this.startValue) * 100;
  }

  private handleVisibilityChange(isVisible: boolean): void {
    const now = Date.now();
    const elapsedSinceLastChange = now - this.lastVisibilityChange;

    if (isVisible) {
      if (elapsedSinceLastChange > 1000) {
        this.endTime += elapsedSinceLastChange;
      }
      this.startTimer();
    } else {
      this.stop();
    }

    this.updateTimer();
  }

  private updateAriaLabel(): void {
    const { hours, minutes, seconds } = this.getTimeComponents();
    this.ariaLabel = `Tempo de sessão: ${hours} horas, ${minutes} minutos e ${seconds} segundos`;
  }

  ngOnDestroy() {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
